import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '../client';
import type {
    AnalyticsGranularity,
    HistoricalValuesResponse,
    HistoricalPnlResponse,
    HistoricalReturnsResponse,
    HistoricalMwrrResponse,
    AggregatedValuesResponse,
    AggregatedPnlResponse,
    AggregatedMwrrResponse,
    LedgerType,
} from '../types';

interface UseLedgerHistoricalValuesParams {
    /** The ledger ID to fetch historical values for */
    ledgerId: number;
    /** Time granularity for aggregation */
    granularity: AnalyticsGranularity;
    /** Optional start date (ISO 8601 or Y-m-d format) */
    start?: string;
    /** Optional end date (ISO 8601 or Y-m-d format) */
    end?: string;
}

/**
 * Hook for fetching historical ledger values for analytics charts.
 * Returns period-end values aggregated by month or year.
 *
 * Features:
 * - Uses staleTime of 60 seconds since analytics data doesn't change frequently
 * - Uses placeholderData to keep previous data visible during refetch for smooth transitions
 * - Builds URL with query parameters for granularity and optional date range
 */
export function useLedgerHistoricalValues({
    ledgerId,
    granularity,
    start,
    end,
}: UseLedgerHistoricalValuesParams) {
    return useQuery({
        queryKey: [
            'ledgers',
            ledgerId,
            'analytics',
            'historical-values',
            { granularity, start, end },
        ],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('granularity', granularity);
            if (start) {
                params.append('start', start);
            }
            if (end) {
                params.append('end', end);
            }

            return api.get<HistoricalValuesResponse>(
                `/api/ledgers/${ledgerId}/values/analytics/historical-values?${params.toString()}`
            );
        },
        enabled: !!ledgerId,
        staleTime: 60000, // 60 seconds - analytics data doesn't change frequently
        placeholderData: keepPreviousData, // Keep previous data during refetch for smooth transitions
    });
}

interface UseLedgerHistoricalPnlParams {
    /** The ledger ID to fetch historical P&L for */
    ledgerId: number;
    /** Time granularity for aggregation */
    granularity: AnalyticsGranularity;
    /** Optional start date (ISO 8601 or Y-m-d format) */
    start?: string;
    /** Optional end date (ISO 8601 or Y-m-d format) */
    end?: string;
}

/**
 * Hook for fetching historical P&L data for analytics charts.
 * Returns period-end P&L values aggregated by month or year.
 *
 * Features:
 * - Uses staleTime of 60 seconds since analytics data doesn't change frequently
 * - Uses placeholderData to keep previous data visible during refetch for smooth transitions
 * - Builds URL with query parameters for granularity and optional date range
 */
export function useLedgerHistoricalPnl({
    ledgerId,
    granularity,
    start,
    end,
}: UseLedgerHistoricalPnlParams) {
    return useQuery({
        queryKey: [
            'ledgers',
            ledgerId,
            'analytics',
            'historical-pnl',
            { granularity, start, end },
        ],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('granularity', granularity);
            if (start) {
                params.append('start', start);
            }
            if (end) {
                params.append('end', end);
            }

            return api.get<HistoricalPnlResponse>(
                `/api/ledgers/${ledgerId}/values/analytics/historical-pnl?${params.toString()}`
            );
        },
        enabled: !!ledgerId,
        staleTime: 60000, // 60 seconds - analytics data doesn't change frequently
        placeholderData: keepPreviousData, // Keep previous data during refetch for smooth transitions
    });
}

interface UseLedgerHistoricalReturnsParams {
    /** The ledger ID to fetch historical returns for */
    ledgerId: number;
    /** Time granularity for aggregation */
    granularity: AnalyticsGranularity;
    /** Optional start date (ISO 8601 or Y-m-d format) */
    start?: string;
    /** Optional end date (ISO 8601 or Y-m-d format) */
    end?: string;
}

/**
 * Hook for fetching historical MWRR data for analytics charts.
 * Returns rolling MWRR calculated from inception to each period end.
 *
 * Features:
 * - Uses staleTime of 60 seconds since analytics data doesn't change frequently
 * - Uses placeholderData to keep previous data visible during refetch for smooth transitions
 * - Returns empty items for debt ledgers or ledgers without returns
 * - Periods with < 3 months data show null
 */
export function useLedgerHistoricalReturns({
    ledgerId,
    granularity,
    start,
    end,
}: UseLedgerHistoricalReturnsParams) {
    return useQuery({
        queryKey: [
            'ledgers',
            ledgerId,
            'analytics',
            'historical-returns',
            { granularity, start, end },
        ],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('granularity', granularity);
            if (start) {
                params.append('start', start);
            }
            if (end) {
                params.append('end', end);
            }

            return api.get<HistoricalReturnsResponse>(
                `/api/ledgers/${ledgerId}/values/analytics/historical-returns?${params.toString()}`
            );
        },
        enabled: !!ledgerId,
        staleTime: 60000, // 60 seconds - analytics data doesn't change frequently
        placeholderData: keepPreviousData, // Keep previous data during refetch for smooth transitions
    });
}

interface UseLedgerHistoricalMwrrParams {
    /** The ledger ID to fetch historical MWRR for */
    ledgerId: number;
    /** Time granularity for aggregation */
    granularity: AnalyticsGranularity;
    /** Optional start date (ISO 8601 or Y-m-d format) */
    start?: string;
    /** Optional end date (ISO 8601 or Y-m-d format) */
    end?: string;
}

/**
 * Hook for fetching historical MWRR data only for analytics charts.
 * Returns rolling MWRR calculated from inception to each period end.
 *
 * Features:
 * - Uses staleTime of 60 seconds since analytics data doesn't change frequently
 * - Uses placeholderData to keep previous data visible during refetch for smooth transitions
 * - Returns empty items for debt ledgers or ledgers without returns
 * - Periods with < 3 months data show null
 */
export function useLedgerHistoricalMwrr({
    ledgerId,
    granularity,
    start,
    end,
}: UseLedgerHistoricalMwrrParams) {
    return useQuery({
        queryKey: [
            'ledgers',
            ledgerId,
            'analytics',
            'historical-mwrr',
            { granularity, start, end },
        ],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('granularity', granularity);
            if (start) {
                params.append('start', start);
            }
            if (end) {
                params.append('end', end);
            }

            return api.get<HistoricalMwrrResponse>(
                `/api/ledgers/${ledgerId}/values/analytics/historical-mwrr?${params.toString()}`
            );
        },
        enabled: !!ledgerId,
        staleTime: 60000, // 60 seconds - analytics data doesn't change frequently
        placeholderData: keepPreviousData, // Keep previous data during refetch for smooth transitions
    });
}

interface UseAggregatedValuesParams {
    /** Ledger type to aggregate (asset or debt) */
    type: LedgerType;
    /** Time granularity for aggregation */
    granularity: AnalyticsGranularity;
    /** Optional collection IDs to filter by */
    collectionIds?: number[];
    /** Optional tag IDs to filter by */
    tagIds?: number[];
    /** Optional start date (ISO 8601 or Y-m-d format) */
    start?: string;
    /** Optional end date (ISO 8601 or Y-m-d format) */
    end?: string;
    /** Whether the query should be enabled */
    enabled?: boolean;
}

/**
 * Hook for fetching aggregated values across multiple ledgers.
 * Used in portfolio-level analytics views (LedgersAnalyticsView).
 *
 * Features:
 * - Aggregates values from all matching ledgers per period
 * - Respects collection and tag filters
 * - Uses staleTime of 60 seconds since analytics data doesn't change frequently
 * - Uses placeholderData to keep previous data visible during refetch for smooth transitions
 */
export function useAggregatedValues({
    type,
    granularity,
    collectionIds = [],
    tagIds = [],
    start,
    end,
    enabled = true,
}: UseAggregatedValuesParams) {
    return useQuery({
        queryKey: [
            'ledgers',
            'analytics',
            'aggregated-values',
            {
                type,
                granularity,
                collections: collectionIds.sort().join(','),
                tags: tagIds.sort().join(','),
                start,
                end,
            },
        ],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('type', type);
            params.append('granularity', granularity);

            if (collectionIds.length > 0) {
                params.append('collections', collectionIds.join(','));
            }

            if (tagIds.length > 0) {
                params.append('tags', tagIds.join(','));
            }

            if (start) {
                params.append('start', start);
            }

            if (end) {
                params.append('end', end);
            }

            return api.get<AggregatedValuesResponse>(
                `/api/ledgers/analytics/aggregated-values?${params.toString()}`
            );
        },
        enabled: enabled,
        staleTime: 60000, // 60 seconds - analytics data doesn't change frequently
        placeholderData: keepPreviousData, // Keep previous data during refetch for smooth transitions
    });
}

interface UseAggregatedPnlParams {
    /** Time granularity for aggregation */
    granularity: AnalyticsGranularity;
    /** Optional collection IDs to filter by */
    collectionIds?: number[];
    /** Optional tag IDs to filter by */
    tagIds?: number[];
    /** Optional start date (ISO 8601 or Y-m-d format) */
    start?: string;
    /** Optional end date (ISO 8601 or Y-m-d format) */
    end?: string;
    /** Whether the query should be enabled */
    enabled?: boolean;
}

/**
 * Hook for fetching aggregated P&L across multiple ledgers.
 * Used in portfolio-level analytics views (LedgersAnalyticsView).
 *
 * Features:
 * - Only works for assets (not debts) - P&L is not meaningful for debt ledgers
 * - Excludes TRACKING_MODE_OTHER ledgers from aggregation
 * - Returns eligibleLedgerCount and excludedLedgerCount for transparency
 * - Respects collection and tag filters
 * - Uses staleTime of 60 seconds since analytics data doesn't change frequently
 * - Uses placeholderData to keep previous data visible during refetch for smooth transitions
 */
export function useAggregatedPnl({
    granularity,
    collectionIds = [],
    tagIds = [],
    start,
    end,
    enabled = true,
}: UseAggregatedPnlParams) {
    return useQuery({
        queryKey: [
            'ledgers',
            'analytics',
            'aggregated-pnl',
            {
                granularity,
                collections: collectionIds.sort().join(','),
                tags: tagIds.sort().join(','),
                start,
                end,
            },
        ],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('granularity', granularity);

            if (collectionIds.length > 0) {
                params.append('collections', collectionIds.join(','));
            }

            if (tagIds.length > 0) {
                params.append('tags', tagIds.join(','));
            }

            if (start) {
                params.append('start', start);
            }

            if (end) {
                params.append('end', end);
            }

            return api.get<AggregatedPnlResponse>(
                `/api/ledgers/analytics/aggregated-pnl?${params.toString()}`
            );
        },
        enabled: enabled,
        staleTime: 60000, // 60 seconds - analytics data doesn't change frequently
        placeholderData: keepPreviousData, // Keep previous data during refetch for smooth transitions
    });
}

interface UseAggregatedMwrrParams {
    /** Time granularity for aggregation */
    granularity: AnalyticsGranularity;
    /** Optional collection IDs to filter by */
    collectionIds?: number[];
    /** Optional tag IDs to filter by */
    tagIds?: number[];
    /** Optional start date (ISO 8601 or Y-m-d format) */
    start?: string;
    /** Optional end date (ISO 8601 or Y-m-d format) */
    end?: string;
    /** Whether the query should be enabled */
    enabled?: boolean;
}

/**
 * Hook for fetching aggregated MWRR across multiple ledgers.
 * Used in portfolio-level analytics views (LedgersAnalyticsView).
 *
 * Features:
 * - Only works for assets (not debts) - MWRR is not meaningful for debt ledgers
 * - Calculates portfolio-level MWRR using combined cash flows and terminal values
 * - Returns eligibleLedgerCount and excludedLedgerCount for transparency
 * - Respects collection and tag filters
 * - Uses staleTime of 60 seconds since analytics data doesn't change frequently
 * - Uses placeholderData to keep previous data visible during refetch for smooth transitions
 */
export function useAggregatedMwrr({
    granularity,
    collectionIds = [],
    tagIds = [],
    start,
    end,
    enabled = true,
}: UseAggregatedMwrrParams) {
    return useQuery({
        queryKey: [
            'ledgers',
            'analytics',
            'aggregated-mwrr',
            {
                granularity,
                collections: collectionIds.sort().join(','),
                tags: tagIds.sort().join(','),
                start,
                end,
            },
        ],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('granularity', granularity);

            if (collectionIds.length > 0) {
                params.append('collections', collectionIds.join(','));
            }

            if (tagIds.length > 0) {
                params.append('tags', tagIds.join(','));
            }

            if (start) {
                params.append('start', start);
            }

            if (end) {
                params.append('end', end);
            }

            return api.get<AggregatedMwrrResponse>(
                `/api/ledgers/analytics/aggregated-mwrr?${params.toString()}`
            );
        },
        enabled: enabled,
        staleTime: 60000, // 60 seconds - analytics data doesn't change frequently
        placeholderData: keepPreviousData, // Keep previous data during refetch for smooth transitions
    });
}

